import React, { useEffect, useState } from "react";
import { Chart } from "primereact/chart";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";

const SCALED_CURRENT_SCENARIO_ID = 9999;

interface ScenarioResult {
  scenario_id: number;
  total_rebate?: number;
  estimated_medicare_reimbursement?: number;
  estimated_pvt_reimbursement?: number;
  net_price?: number;
  invoice_units?: number;
  totalNCR?: number; // precomputed NCR (medicare)
  totalWeightedNCR?: number; // precomputed weighted NCR
  totalPrice?: number; // newly passed precomputed total price (preferred over computing locally)
}

interface ScenarioChartProps {
  data: ScenarioResult[];
  pageSize?: number; // default to 10
  isHomePage?: boolean; // default false if not provided
  category?: string; // optional category to display in title
  ncrMode?: 'medicare' | 'weighted'; // determines which NCR/reimbursement fields to use
  getScenarioLabel?: (scenarioId: number) => string; // optional custom label resolver (e.g. "Current @ +15%")
}

const COLOR_PALETTE = [
  "#F59E0B", // Amber-500 (Rebate)
  "#173A63", // Company blue (Reimb)
  "#10B981", // Green-500 (Price)
];

const NAV_BUTTON_STYLE: React.CSSProperties = {
  backgroundColor: "transparent",
  border: "none",
  color: "#173A63",
  padding: "0.25rem",
  borderRadius: "0",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color 0.2s ease, opacity 0.2s ease",
};

const ScenarioChart: React.FC<ScenarioChartProps> = ({
  data,
  pageSize = 10,
  isHomePage = false,
  category,
  ncrMode = 'medicare',
  getScenarioLabel,
}) => {
  const [page, setPage] = useState(0);
  const [pagedData, setPagedData] = useState<ScenarioResult[]>([]);
  const [maxY, setMaxY] = useState(100);
  const [chartData, setChartData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState<any>({});

  const boxShadow = isHomePage ? "none" : "0 4px 12px rgba(0, 0, 0, 0.1)";

  const handleNavHover = (
    event: React.MouseEvent<HTMLButtonElement>,
    isDisabled: boolean,
    isHover: boolean
  ) => {
    if (isDisabled) return;
    event.currentTarget.style.color = isHover ? "#0f2847" : "#173A63";
  };

  useEffect(() => {
    const slice = data.slice(page * pageSize, (page + 1) * pageSize);
    setPagedData(slice);
    const maxTotalValue = Math.max(
      ...slice.map((s) => {
        const rebate = s.total_rebate || 0;
        const reimb = (ncrMode === 'medicare' ? s.estimated_medicare_reimbursement : s.estimated_pvt_reimbursement) || 0;
        // Prefer precomputed totalPrice; fallback to local calculation if absent
        const price = s.net_price || 0 ;
        const ncrValue = ncrMode === 'medicare' ? (s.totalNCR || 0) : (s.totalWeightedNCR || 0);
        return Math.max(rebate, reimb, price, ncrValue);
      })
    );
    setMaxY(Math.ceil((maxTotalValue / 1000) * 1.1));
  }, [page, pageSize, data, ncrMode]);

  // Reset to first page whenever dataset or category context changes significantly
  useEffect(() => {
    setPage(0);
  }, [data.length, category]);

  useEffect(() => {
    if (pagedData.length) {
      const labels = pagedData.map((s) =>
  getScenarioLabel ? getScenarioLabel(s.scenario_id) : (s.scenario_id === 0 ? "Current" : s.scenario_id === SCALED_CURRENT_SCENARIO_ID ? "Current (scaled)" : `Scenario ${s.scenario_id}`)
);


      const data = {
        labels,
        datasets: [
          {
            label: ncrMode === 'medicare' ? 'NCR (K $)' : 'Weighted NCR (K $)',
            data: pagedData.map((s) => (ncrMode === 'medicare' ? (s.totalNCR || 0) : (s.totalWeightedNCR || 0)) / 1000),
            type: "line",
            borderColor: "red",
            borderWidth: 2,
            fill: false,
            tension: 0.3, // smooth curve
            pointBackgroundColor: "red",
            pointRadius: 4,
          },
          {
            label: "Rebate (K $)",
            data: pagedData.map((s) => (s.total_rebate || 0) / 1000),
            backgroundColor: COLOR_PALETTE[0],
            type: "bar",
          },
          {
            label: ncrMode === 'medicare' ? 'Medicare Reimb (K $)' : 'Weighted Reimb (K $)',
            data: pagedData.map((s) => ((ncrMode === 'medicare' ? s.estimated_medicare_reimbursement : s.estimated_pvt_reimbursement) || 0) / 1000),
            backgroundColor: COLOR_PALETTE[1],
            type: "bar",
          },
          {
            label: "Price (K $)",
            data: pagedData.map((s) => (s.net_price || 0) / 1000),
            backgroundColor: COLOR_PALETTE[2],
            type: "bar",
          },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            mode: "index" as const,
            intersect: false,
            callbacks: {
              label: (context: any) => {
                const yVal = context?.parsed?.y;
                const formatted = typeof yVal === 'number' ? yVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : yVal;
                return `${context.dataset.label}: $${formatted}K`;
              },
            },
          },
          legend: {
            position: "top" as const,
          },
          title: {
            display: true,
            text: "Key Metrics",
            color: "#173A63",
            font: {
              size: 18,
              weight: "bold",
            },
          },
        },
        scales: {
          x: {
            stacked: false,
            title: {
              display: true,
              text: "",
              font: { size: 14, weight: "bold" },
            },
          },
          y: {
            beginAtZero: true,
            max: maxY,
            ticks: {
              stepSize: Math.ceil(maxY / 8),
              callback: (value: any) => `${typeof value === 'number' ? value.toLocaleString('en-US') : value}K`,
            },
            title: {
              display: true,
              text: "Amount (Thousands USD)",
              font: { size: 14, weight: "bold" },
            },
          },
        },
      };

      setChartData(data);
      setChartOptions(options);
    }
  }, [pagedData, maxY, ncrMode]);

  const totalPages = Math.ceil(data.length / pageSize);

  return (
    <Card
  title={`Scenario Performance Analysis${category ? ' - ' + category : ''} (${ncrMode === 'medicare' ? 'Medicare NCR' : 'Weighted NCR'})`}
      style={{ minHeight: 450, border: "none", boxShadow: boxShadow }}
    >
      <div style={{ height: 400, width: "100%" }}>
        {chartData ? (
          <Chart
            type="bar"
            data={chartData}
            options={chartOptions}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <div className="flex align-items-center justify-content-center" style={{ height: '100%' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          </div>
        )}
      </div>

      {/* Pagination */}
      <div
        style={{
          marginTop: 12,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          type="button"
          aria-label="Previous page"
          style={{
            ...NAV_BUTTON_STYLE,
            opacity: page === 0 ? 0.5 : 1,
            cursor: page === 0 ? "not-allowed" : "pointer",
          }}
          onMouseEnter={(event) => handleNavHover(event, page === 0, true)}
          onMouseLeave={(event) => handleNavHover(event, page === 0, false)}
          onClick={() => setPage((p) => Math.max(p - 1, 0))}
          disabled={page === 0}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 12L16 5V19L8 12Z" />
          </svg>
        </button>
        <span style={{ fontWeight: 600 }}>
          Page {page + 1} of {totalPages}
        </span>
        <button
          type="button"
          aria-label="Next page"
          style={{
            ...NAV_BUTTON_STYLE,
            opacity: page === totalPages - 1 ? 0.5 : 1,
            cursor: page === totalPages - 1 ? "not-allowed" : "pointer",
          }}
          onMouseEnter={(event) => handleNavHover(event, page === totalPages - 1, true)}
          onMouseLeave={(event) => handleNavHover(event, page === totalPages - 1, false)}
          onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
          disabled={page === totalPages - 1}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M16 12L8 19V5L16 12Z" />
          </svg>
        </button>
      </div>
    </Card>
  );
};

export default ScenarioChart;